// src/utils/canvasPassGenerator.js
import { trackDownload } from '../services/ticketApi';
import bigLogoAsset from '../assets/Big.png';
import horizontalLogoAsset from '../assets/logo-xolofest-horizontal.png';
import poweredByAsset from '../assets/xolos-five-black.png';

/**
 * Carga de forma asíncrona una imagen en un elemento HTMLImageElement
 * @param {string} src - Ruta o URL de la imagen
 * @returns {Promise<HTMLImageElement|null>}
 */
function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (err) => {
      console.warn(`[CanvasPassGenerator] No se pudo cargar la imagen: ${src}`, err);
      resolve(null);
    };
    img.src = src;
  });
}

/**
 * Traza un rectángulo con esquinas redondeadas
 */
function drawRoundRect(ctx, x, y, width, height, radius) {
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, radius);
    return;
  }
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Trunca un texto si excede el ancho máximo disponible
 */
function fitText(ctx, text, maxWidth) {
  if (!text) return '';
  if (ctx.measureText(text).width <= maxWidth) return text;
  let truncated = text;
  while (truncated.length > 0 && ctx.measureText(`${truncated}...`).width > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated}...`;
}

/**
 * Genera y descarga el Pase Digital oficial como imagen PNG de 2160x2940px (Escala 3x Ultra HD Máxima Fidelidad)
 * @param {Object} passData - Datos del pase registrado
 * @param {HTMLCanvasElement|null} qrSourceElement - Elemento canvas del QR ya renderizado en el DOM
 * @returns {Promise<{success: boolean, fileName: string}>}
 */
export async function generatePassImage(passData, qrSourceElement = null) {
  if (!passData) {
    throw new Error('Datos del pase no proporcionados.');
  }

  // Dimensiones base lógicas y resolución física 3x (2160 × 2940 px)
  const LOGICAL_WIDTH = 720;
  const LOGICAL_HEIGHT = 980;
  const scale = 3;
  const WIDTH = LOGICAL_WIDTH * scale;   // 2160px
  const HEIGHT = LOGICAL_HEIGHT * scale; // 2940px

  const paddedFolio = (passData.folio || passData.ticketFolio || '0000').toString().padStart(4, '0');
  const isVip = (passData.category || '').toUpperCase() === 'VIP';
  const categoryLabel = isVip ? '★ VIP EXCLUSIVE ★' : 'ACCESO GENERAL';
  const raffleCode = passData.raffleTicketCode || passData.raffle_code || passData.raffleCode || `XOLO-${paddedFolio}`;
  const fullName = (passData.fullName || 'Aficionado Xoloitzcuintle').trim();

  // Formato de fecha
  let formattedDate = '07 NOV 2026';
  try {
    const rawDate = passData.registeredAt ? new Date(passData.registeredAt) : new Date();
    if (!isNaN(rawDate.getTime())) {
      const day = rawDate.getDate().toString().padStart(2, '0');
      const months = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
      const month = months[rawDate.getMonth()];
      const year = rawDate.getFullYear();
      formattedDate = `${day} ${month} ${year}`;
    }
  } catch (e) {
    formattedDate = '07 NOV 2026';
  }

  // Crear lienzo ultra HD de 2160 × 2940 px (Escala 3x)
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('No se pudo inicializar el contexto 2D del Canvas.');
  }

  // Configuración de renderizado de ultra alta resolución
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Aplicar escala 3x para dibujar usando coordenadas lógicas (720x980)
  ctx.scale(scale, scale);

  const W = LOGICAL_WIDTH;
  const H = LOGICAL_HEIGHT;

  // Precargar imágenes institucionales
  const [horizontalLogoImg, bigShieldImg, poweredByImg] = await Promise.all([
    loadImage(horizontalLogoAsset || '/img/brand/logo-xolofest-horizontal.png'),
    loadImage(bigLogoAsset || '/img/Big.png'),
    loadImage(poweredByAsset || '/img/page/xolos-five-black.png')
  ]);

  // 1. FONDO BASE Y DEGRADADO OFICIAL (Obsidiana #09090b a #18181b)
  ctx.fillStyle = '#09090b';
  ctx.fillRect(0, 0, W, H);

  // Degradado interno de la tarjeta
  const cardGradient = ctx.createLinearGradient(0, 20, 0, H - 20);
  cardGradient.addColorStop(0, '#0b090c');
  cardGradient.addColorStop(0.35, '#131117');
  cardGradient.addColorStop(1, '#18181b');

  const cardMargin = 22;
  const cardWidth = W - cardMargin * 2;
  const cardHeight = H - cardMargin * 2;
  const cardRadius = 26;

  drawRoundRect(ctx, cardMargin, cardMargin, cardWidth, cardHeight, cardRadius);
  ctx.fillStyle = cardGradient;
  ctx.fill();

  // Resplandor radial superior rojinegro / blanco según categoría
  const glowGradient = ctx.createRadialGradient(W / 2, 100, 10, W / 2, 100, 260);
  if (isVip) {
    glowGradient.addColorStop(0, 'rgba(245, 158, 11, 0.22)');
    glowGradient.addColorStop(1, 'rgba(245, 158, 11, 0)');
  } else {
    glowGradient.addColorStop(0, 'rgba(220, 38, 38, 0.25)');
    glowGradient.addColorStop(1, 'rgba(220, 38, 38, 0)');
  }
  ctx.fillStyle = glowGradient;
  ctx.fillRect(cardMargin, cardMargin, cardWidth, 340);

  // 2. BORDE EXTERNO DE LA TARJETA (Rojo carmesí #dc2626 o Blanco/Plata para VIP)
  const borderGradient = ctx.createLinearGradient(cardMargin, cardMargin, cardMargin + cardWidth, cardMargin + cardHeight);
  if (isVip) {
    borderGradient.addColorStop(0, '#ffffff');
    borderGradient.addColorStop(0.5, '#e4e4e7');
    borderGradient.addColorStop(1, '#71717a');
  } else {
    borderGradient.addColorStop(0, '#dc2626');
    borderGradient.addColorStop(0.5, '#ef4444');
    borderGradient.addColorStop(1, '#991b1b');
  }
  drawRoundRect(ctx, cardMargin, cardMargin, cardWidth, cardHeight, cardRadius);
  ctx.lineWidth = 3;
  ctx.strokeStyle = borderGradient;
  ctx.stroke();

  // 3. CABECERA: Logotipo Horizontal Oficial 'logo-xolofest-horizontal.png'
  if (horizontalLogoImg) {
    const logoH = 78;
    const aspect = horizontalLogoImg.naturalWidth && horizontalLogoImg.naturalHeight
      ? horizontalLogoImg.naturalWidth / horizontalLogoImg.naturalHeight
      : 3.08;
    const logoW = Math.round(logoH * aspect);
    const logoX = (W - logoW) / 2;
    const logoY = 46;
    ctx.drawImage(horizontalLogoImg, logoX, logoY, logoW, logoH);
  } else if (bigShieldImg) {
    // Respaldo de escudo circular si no estuviese el horizontal
    const shieldSize = 78;
    const shieldX = (W - shieldSize) / 2;
    const shieldY = 46;
    ctx.drawImage(bigShieldImg, shieldX, shieldY, shieldSize, shieldSize);
    ctx.textAlign = 'center';
    ctx.fillStyle = '#ffffff';
    ctx.font = '900 32px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
    ctx.fillText('XOLO FEST 2026', W / 2, 148);
  }

  // Subtítulo conmemorativo oficial
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.70)';
  ctx.font = '700 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillText('PASE DIGITAL CONMEMORATIVO OFICIAL', W / 2, 148);

  // 4. BADGE DE CATEGORÍA: ACCESO GENERAL (#dc2626) o VIP EXCLUSIVE (#ffffff)
  const badgeWidth = isVip ? 210 : 200;
  const badgeHeight = 34;
  const badgeX = (W - badgeWidth) / 2;
  const badgeY = 168;
  const badgeRadius = 17;

  drawRoundRect(ctx, badgeX, badgeY, badgeWidth, badgeHeight, badgeRadius);
  ctx.fillStyle = isVip ? '#ffffff' : '#dc2626';
  ctx.fill();

  ctx.textAlign = 'center';
  ctx.fillStyle = isVip ? '#09090b' : '#ffffff';
  ctx.font = '800 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillText(categoryLabel, W / 2, badgeY + 22);

  // 5. BLOQUE CENTRAL: CÓDIGO QR DE ALTA DENSIDAD CON ESCUDO 'Big.png' EXCAVADO AL CENTRO
  const qrBoxSize = 280;
  const qrBoxX = (W - qrBoxSize) / 2;
  const qrBoxY = 224;
  const qrBoxRadius = 18;

  // Tarjeta blanca para albergar el código QR
  drawRoundRect(ctx, qrBoxX, qrBoxY, qrBoxSize, qrBoxSize, qrBoxRadius);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = isVip ? 'rgba(245, 158, 11, 0.4)' : 'rgba(220, 38, 38, 0.4)';
  ctx.stroke();

  // Obtener fuente del QR (desde parámetro o elemento DOM)
  const qrCanvas =
    qrSourceElement ||
    document.getElementById('ticket-qr-canvas') ||
    document.querySelector('#ticket-qr-canvas') ||
    document.querySelector('.qr-code-wrapper canvas');

  const qrInnerSize = 250;
  const qrInnerX = (W - qrInnerSize) / 2;
  const qrInnerY = qrBoxY + (qrBoxSize - qrInnerSize) / 2;

  if (qrCanvas && qrCanvas.width > 0) {
    ctx.drawImage(qrCanvas, qrInnerX, qrInnerY, qrInnerSize, qrInnerSize);
  } else {
    // Si no está el canvas en el DOM, renderizar contenedor estilizado
    ctx.fillStyle = '#09090b';
    ctx.fillRect(qrInnerX + 20, qrInnerY + 20, qrInnerSize - 40, qrInnerSize - 40);
  }

  // Reforzar escudo circular oficial 'Big.png' al centro del QR excavado con marco blanco y esquinas redondeadas
  const centerLogoSize = 62;
  const centerLogoX = (W - centerLogoSize) / 2;
  const centerLogoY = qrBoxY + (qrBoxSize - centerLogoSize) / 2;

  // Marco blanco con esquinas redondeadas debajo del logo central
  drawRoundRect(ctx, centerLogoX - 4, centerLogoY - 4, centerLogoSize + 8, centerLogoSize + 8, 12);
  ctx.fillStyle = '#ffffff';
  ctx.fill();

  if (bigShieldImg) {
    ctx.drawImage(bigShieldImg, centerLogoX, centerLogoY, centerLogoSize, centerLogoSize);
  }

  // Texto de ayuda bajo el QR
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.font = '600 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillText('Presenta este código en los accesos del Estacionamiento del Estadio Caliente', W / 2, 532);

  // 6. BLOQUE DE FOLIOS: Folio de Boleto (#0428) y Código de Tómbola Perrona (passData.raffleTicketCode ej: XOLO-B4WR)
  const folioBoxY = 552;
  const folioBoxHeight = 88;
  const folioBoxWidth = 300;
  const leftFolioBoxX = cardMargin + 24;
  const rightFolioBoxX = W - cardMargin - 24 - folioBoxWidth;

  // Tarjeta izquierda: Folio de Boleto
  drawRoundRect(ctx, leftFolioBoxX, folioBoxY, folioBoxWidth, folioBoxHeight, 14);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.04)';
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.stroke();

  ctx.textAlign = 'center';
  ctx.fillStyle = '#9ca3af';
  ctx.font = '700 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillText('FOLIO DE BOLETO', leftFolioBoxX + folioBoxWidth / 2, folioBoxY + 26);

  ctx.fillStyle = '#ffffff';
  ctx.font = '900 30px "Courier New", Courier, monospace';
  ctx.fillText(`#${paddedFolio}`, leftFolioBoxX + folioBoxWidth / 2, folioBoxY + 66);

  // Tarjeta derecha: Bloque de Tómbola Perrona (Código alfanumérico generado)
  drawRoundRect(ctx, rightFolioBoxX, folioBoxY, folioBoxWidth, folioBoxHeight, 14);
  ctx.fillStyle = 'rgba(220, 38, 38, 0.1)';
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = 'rgba(220, 38, 38, 0.4)';
  ctx.stroke();

  ctx.fillStyle = '#dc2626';
  ctx.font = '800 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillText('TÓMBOLA PERRONA', rightFolioBoxX + folioBoxWidth / 2, folioBoxY + 26);

  ctx.fillStyle = '#ffffff';
  ctx.font = '900 25px "Courier New", Courier, monospace';
  ctx.fillText(raffleCode, rightFolioBoxX + folioBoxWidth / 2, folioBoxY + 66);

  // 7. LÍNEA DIVISORIA PERFORADA ESTILO TICKET DE ESTADIO
  const dividerY = 662;
  ctx.beginPath();
  ctx.setLineDash([7, 7]);
  ctx.moveTo(cardMargin + 32, dividerY);
  ctx.lineTo(W - cardMargin - 32, dividerY);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.16)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.setLineDash([]); // Reset line dash

  // Muescas semicirculares de ticket en los bordes
  const notchRadius = 15;
  ctx.fillStyle = '#09090b';
  ctx.beginPath();
  ctx.arc(cardMargin, dividerY, notchRadius, -Math.PI / 2, Math.PI / 2);
  ctx.fill();
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = isVip ? '#ffffff' : '#dc2626';
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(W - cardMargin, dividerY, notchRadius, Math.PI / 2, -Math.PI / 2);
  ctx.fill();
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = isVip ? '#ffffff' : '#dc2626';
  ctx.stroke();

  // 8. DATOS DEL AFICIONADO: Nombre Completo y fecha
  const dataBoxY = 684;
  const dataBoxWidth = W - (cardMargin + 24) * 2;
  const dataBoxHeight = 104;
  const dataBoxX = cardMargin + 24;

  drawRoundRect(ctx, dataBoxX, dataBoxY, dataBoxWidth, dataBoxHeight, 14);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.stroke();

  // Columna Izquierda: Titular
  ctx.textAlign = 'left';
  ctx.fillStyle = '#71717a';
  ctx.font = '700 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillText('TITULAR DEL ACCESO', dataBoxX + 24, dataBoxY + 28);

  ctx.fillStyle = '#ffffff';
  ctx.font = '800 22px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  const fittedName = fitText(ctx, fullName, 360);
  ctx.fillText(fittedName, dataBoxX + 24, dataBoxY + 58);

  // Teléfono o Correo visible
  const contactText = passData.phone
    ? `Tel: ${passData.phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}`
    : (passData.email || 'Acceso Registrado');
  ctx.fillStyle = '#a1a1aa';
  ctx.font = '600 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillText(contactText, dataBoxX + 24, dataBoxY + 84);

  // Columna Derecha: Fecha de Registro y Estatus
  ctx.textAlign = 'right';
  ctx.fillStyle = '#71717a';
  ctx.font = '700 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillText('EMISIÓN OFICIAL', dataBoxX + dataBoxWidth - 24, dataBoxY + 28);

  ctx.fillStyle = '#ffffff';
  ctx.font = '800 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillText(formattedDate, dataBoxX + dataBoxWidth - 24, dataBoxY + 56);

  ctx.fillStyle = '#10b981';
  ctx.font = '800 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillText('✓ VERIFICADO', dataBoxX + dataBoxWidth - 24, dataBoxY + 84);

  // 9. PIE DE TARJETA: Distintivo "Powered by" con logotipo oficial 'xolos-five-black.png'
  const poweredByY = 822;
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
  ctx.font = '800 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillText('P O W E R E D   B Y', W / 2, poweredByY);

  // Contenedor blanco para el logotipo 'xolos-five-black.png'
  const logoPillWidth = 260;
  const logoPillHeight = 62;
  const logoPillX = (W - logoPillWidth) / 2;
  const logoPillY = 838;

  drawRoundRect(ctx, logoPillX, logoPillY, logoPillWidth, logoPillHeight, 16);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
  ctx.stroke();

  if (poweredByImg) {
    // Relación de aspecto 700x161 = 4.347
    const imgH = 44;
    const imgW = Math.round(imgH * (poweredByImg.naturalWidth / poweredByImg.naturalHeight || 4.347));
    const imgX = (W - imgW) / 2;
    const imgY = logoPillY + (logoPillHeight - imgH) / 2;
    ctx.drawImage(poweredByImg, imgX, imgY, imgW, imgH);
  }

  // 10. CONVERTIR A BLOB PNG EN RESOLUCIÓN 2160x2940px (3x) Y DESCARGAR AUTOMÁTICAMENTE
  const fileName = `Pase_XoloFest_${paddedFolio}.png`;

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => {
      if (b) {
        resolve(b);
      } else {
        reject(new Error('Fallo al exportar Canvas a formato Blob PNG'));
      }
    }, 'image/png');
  });

  const blobUrl = URL.createObjectURL(blob);
  const downloadLink = document.createElement('a');
  downloadLink.href = blobUrl;
  downloadLink.download = fileName;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);

  // 11. DISPARAR LLAMADA SILENCIOSA A 'ticketApi.trackDownload(folio)'
  try {
    await trackDownload(paddedFolio);
  } catch (err) {
    console.warn('[CanvasPassGenerator] Error silencioso al reportar telemetría de descarga:', err);
  }

  return {
    success: true,
    fileName: fileName
  };
}

export default generatePassImage;
